import { Injectable, OnDestroy, inject } from "@angular/core";
import { BehaviorSubject, Observable, Subject, distinctUntilChanged } from "rxjs";
import { PLATFORM_ADAPTER, PlatformAdapter } from "../core/platform/platform.adapter";
import { ClientTokenService } from "../services/auth/client-token.service";
import { ClientMsg, Op, ServerMsg } from "./ws-envelope";

export type SyncStatus = "idle" | "connecting" | "open" | "closed" | "error";

// Caps the exponent in `2 ** attempt` to avoid Infinity after long outages.
// The retry loop itself does NOT terminate — for the planning-room UX a
// connection that has been down for an hour should still recover the
// moment the network comes back. Consumers see the status stream cycle
// `closed` → `connecting` → ... and surface that to the UI themselves.
const RECONNECT_BACKOFF_EXP_CAP = 15;

interface WsHandlers {
  open: () => void;
  message: (ev: MessageEvent) => void;
  error: () => void;
  close: () => void;
}

/**
 * Gateway to efb-app-server's /ws/sync. Used by both the web SPA and — since
 * the peer-sync enablement — native iPads (driven by NativeSyncService).
 * openSocket() still no-ops when the platform adapter has no sync URL
 * configured (e.g. prod native until the app-server host is wired).
 */
@Injectable({ providedIn: "root" })
export class SyncWsClientService implements OnDestroy {
  private platform = inject<PlatformAdapter>(PLATFORM_ADAPTER);
  private clientToken = inject(ClientTokenService);

  private socket: WebSocket | null = null;
  private handlers: WsHandlers | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private wantsConnection = false;
  private token: string | null = null;

  private readonly _status$ = new BehaviorSubject<SyncStatus>("idle");
  private readonly _messages$ = new Subject<ServerMsg>();

  readonly status$: Observable<SyncStatus> = this._status$.pipe(distinctUntilChanged());
  readonly messages$: Observable<ServerMsg> = this._messages$.asObservable();

  /** Connect to /ws/sync with the supplied JWT. Calling again with a new
   * token forces a reconnect; with the same token it's a no-op. */
  connect(token: string): void {
    if (!token) return;

    if (this.token === token && this.socket && this.socket.readyState <= WebSocket.OPEN) {
      return;
    }

    this.token = token;
    this.wantsConnection = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;
    this.tearDownSocket();
    this.openSocket();
  }

  disconnect(): void {
    this.wantsConnection = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.tearDownSocket();
    this._status$.next("closed");
  }

  /** Replace the socket with a new one, if a connection is wanted. For an app
   * resume: iOS cuts the TCP connection of a suspended app, and the WebView can
   * keep the socket OPEN with no close event, so nothing else reconnects. */
  reconnect(): void {
    if (!this.wantsConnection || !this.token) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;
    this.tearDownSocket();
    this._status$.next("closed");
    this.openSocket();
  }

  send(msg: ClientMsg): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(msg));
    return true;
  }

  apply(ops: Op[]): boolean {
    return this.send({ type: "APPLY", ops });
  }

  /** `generation`: the native device's sync generation. The web client sends none. */
  subscribe(registrationId: string, sinceSeq: number, generation?: number): boolean {
    return this.send({
      type: "SUBSCRIBE",
      registrationId,
      sinceSeq,
      ...(generation === undefined ? {} : { generation }),
    });
  }

  backfill(registrationId: string, limit: number): boolean {
    return this.send({ type: "BACKFILL", registrationId, limit });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private openSocket(): void {
    const baseUrl = this.platform.syncWebSocketUrl();
    if (!baseUrl || !this.token) return;

    // The server verifies the JWT only during the WS upgrade, so an expired
    // token fails the handshake silently and the reconnect loop would spin
    // forever re-presenting it. Detect expiry up front: stop the loop. On native
    // also ask AuthService to renew the binding token (`POST /auth/renew-binding`).
    // The new token arrives via tokenObs → NativeSyncService → connect(), which
    // re-arms wantsConnection and reopens. This socket is not the only one that
    // asks: every read that finds the session expired asks too
    // (`ClientTokenService.activeSession`). On web the 401 → /login flow
    // (DatabaseService.browser) drives re-auth, so this socket only stops there.
    if (this.clientToken.isTokenExpired(this.token)) {
      this.wantsConnection = false;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this._status$.next("error");
      if (this.platform.isNative) this.clientToken.requestRefresh();
      return;
    }

    const url = `${baseUrl}?token=${encodeURIComponent(this.token)}`;

    this._status$.next("connecting");
    const ws = new WebSocket(url);
    const handlers: WsHandlers = {
      open: () => {
        this.reconnectAttempt = 0;
        this._status$.next("open");
      },
      message: (ev) => {
        let parsed: ServerMsg;
        try {
          parsed = JSON.parse(typeof ev.data === "string" ? ev.data : String(ev.data)) as ServerMsg;
        } catch (err) {
          console.warn("SyncWs: ignoring non-JSON frame", err);
          return;
        }
        this._messages$.next(parsed);
      },
      error: () => {
        this._status$.next("error");
      },
      close: () => {
        if (this.socket === ws) {
          this.detachHandlers(ws, handlers);
          this.socket = null;
          this.handlers = null;
        }
        this._status$.next("closed");
        if (this.wantsConnection) this.scheduleReconnect();
      },
    };
    ws.addEventListener("open", handlers.open);
    ws.addEventListener("message", handlers.message);
    ws.addEventListener("error", handlers.error);
    ws.addEventListener("close", handlers.close);

    this.socket = ws;
    this.handlers = handlers;
  }

  private tearDownSocket(): void {
    const ws = this.socket;
    const handlers = this.handlers;
    this.socket = null;
    this.handlers = null;
    if (!ws) return;
    if (handlers) this.detachHandlers(ws, handlers);
    try {
      ws.close();
    } catch {
      // ignore
    }
  }

  private detachHandlers(ws: WebSocket, handlers: WsHandlers): void {
    ws.removeEventListener("open", handlers.open);
    ws.removeEventListener("message", handlers.message);
    ws.removeEventListener("error", handlers.error);
    ws.removeEventListener("close", handlers.close);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const attempt = Math.min(this.reconnectAttempt, RECONNECT_BACKOFF_EXP_CAP);
    const delay = Math.min(1000 * 2 ** attempt, 30_000);
    this.reconnectAttempt = attempt + 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wantsConnection) this.openSocket();
    }, delay);
  }
}
